import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { formatNumber } from '@/common/utils/formatNumber';
import { getActivePrice } from '../../utils/prices';
import type { BillingAddress } from '@/features/billing-addresses/services/schemas';
import type { Product, Variant } from '../../services/schemas';

interface SubscriptionDetailsProps {
  product: Product;
  variant: Variant;
  billingAddresses: BillingAddress[];
}

export function SubscriptionDetails({
  product,
  variant,
  billingAddresses,
}: SubscriptionDetailsProps) {
  const price = getActivePrice(variant.priceSet);
  const isMonthly = product.billingFrequency === 'monthly';
  const amount = price ? (isMonthly ? price.priceMonthly : price.priceAnnual) : null;
  const periodLabel = isMonthly ? t`mo` : t`yr`;
  const billingContact = billingAddresses.find((a) => a.id === product.billingContact);
  const billingContactLabel = billingContact
    ? (billingContact.name ?? `${billingContact.street}, ${billingContact.city}`)
    : null;

  const rows = [
    { label: t`Variant`, value: variant.name },
    {
      label: t`Billing frequency`,
      value: isMonthly ? t`Monthly` : t`Annual`,
    },
    ...(amount !== null && price
      ? [
          {
            label: t`Price`,
            value: `${formatNumber(amount, 'currency', { currency: 'CZK' })} / ${periodLabel}`,
          },
        ]
      : []),
    ...(billingContactLabel ? [{ label: t`Billing contact`, value: billingContactLabel }] : []),
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 divide-y divide-gray-200">
      <div className="px-4 py-2.5">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
          <Trans>Subscription details</Trans>
        </span>
      </div>
      {rows.map(({ label, value }) => (
        <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
          <span className="text-gray-500">{label}</span>
          <span className="font-medium text-gray-800">{value}</span>
        </div>
      ))}
    </div>
  );
}
